import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  createSearchParams,
  URLSearchParamsInit,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import ProjectsFilter from './ProjectsFilter';
import MembersFilter from './MembersFilter';
import { ReportsFilterProps } from 'components/reports/timeAndActivity/TimeAndActivityReportsFilter/TimeAndActivityReportsFilter';
import CustomCheckbox from 'components/shared/forms/CustomCheckbox';
import Button from 'components/shared/Button';
import { IAmountsOwedReportsURLSearchParams } from 'features/amountsOwedReports/amountsOwedReportsTypes';
import { selectInfoUserSelector } from 'features/user/userSlice';
import { USER_ROLE } from 'features/user/userTypes';
import styles from './AmountsOwedReportsFilter.module.scss';

const AmountsOwedReportsFilter: FC<ReportsFilterProps> = ({ closeFilters }) => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const userInfo = useSelector(selectInfoUserSelector);
  const navigate = useNavigate();
  const isMountedRef = useRef(false);
  const isMembersFilterAvailable = userInfo?.role !== USER_ROLE.User;
  const isMyReportsTab = pathname.endsWith('/my');
  const sumDateRangesQuery = searchParams.get('sumDateRanges');
  const sumDetailedReportsQuery = searchParams.get('detailedReports');
  const noTimeOffQuery = searchParams.get('noTimeOff');

  const defaultSumDateRanges = !!(
    sumDateRangesQuery && sumDateRangesQuery === 'true'
  );
  const defaultDetailedReports = !!(
    sumDetailedReportsQuery && sumDetailedReportsQuery === 'true'
  );
  const defaultNoTimeOff = !!(
    noTimeOffQuery && noTimeOffQuery === 'true'
  );

  const [sumDateRanges, setSumDateRanges] = useState(defaultSumDateRanges);
  const [detailedReports, setDetailedReports] = useState(defaultDetailedReports);
  const [noTimeOff, setNoTimeOff] = useState(defaultNoTimeOff);

  const handleSumDateRangesChange = () => setSumDateRanges(!sumDateRanges);
  const handleDetailedReportsChange = () => setDetailedReports(!detailedReports);
  const handleNoTimeOffChange = () => setNoTimeOff(!noTimeOff);

  const clearFilters = () => {
    const params = Object.fromEntries([...Array.from(searchParams)]);
    const URLSearchParams: IAmountsOwedReportsURLSearchParams = {
      ...params,
    };

    delete URLSearchParams.sumDateRanges;
    delete URLSearchParams.detailedReports;
    delete URLSearchParams.noTimeOff;
    delete URLSearchParams.projectsIds;
    delete URLSearchParams.membersIds;

    navigate({
      pathname: pathname,
      search: createSearchParams(
        URLSearchParams as URLSearchParamsInit
      ).toString(),
    });
    closeFilters();
  };

  useEffect(() => {
    if (isMountedRef.current) {
      const params = Object.fromEntries([...Array.from(searchParams)]);
      const URLSearchParams: IAmountsOwedReportsURLSearchParams = {
        ...params,
        projectsIds: searchParams.getAll('projectsIds'),
        membersIds: searchParams.getAll('membersIds'),
      };

      if (sumDateRanges) {
        URLSearchParams.sumDateRanges = String(sumDateRanges);
      } else {
        delete URLSearchParams.sumDateRanges;
      }

      if (detailedReports) {
        URLSearchParams.detailedReports = String(detailedReports);
      } else {
        delete URLSearchParams.detailedReports;
      }

      if (noTimeOff) {
        URLSearchParams.noTimeOff = String(noTimeOff);
      } else {
        delete URLSearchParams.noTimeOff;
      }

      navigate({
        pathname: pathname,
        search: createSearchParams(
          URLSearchParams as URLSearchParamsInit
        ).toString(),
      });
    }
    isMountedRef.current = true;
  }, [sumDateRanges, detailedReports, noTimeOff]);

  return (
    <>
      <div className={styles.wrapper}>
        <ProjectsFilter />
        {isMembersFilterAvailable && !isMyReportsTab && <MembersFilter />}

        <CustomCheckbox
          label="Sum date ranges"
          isChecked={sumDateRanges}
          handleChange={handleSumDateRangesChange}
        />
        <CustomCheckbox
          label="Detailed reports"
          isChecked={detailedReports}
          handleChange={handleDetailedReportsChange}
        />
        <CustomCheckbox
          label="No time-off"
          isChecked={noTimeOff}
          handleChange={handleNoTimeOffChange}
        />

        <Button onClick={clearFilters}>Clear filters</Button>
        <Button variant="primary" onClick={closeFilters}>Apply filters</Button>
      </div>
    </>
  );
};

export default AmountsOwedReportsFilter;
